import { ReactNode, ReactPortal, useEffect, useMemo } from "react";
import { ReactQueryProvider } from "./ReactQuery";
import { AccountProvider } from "./Account";
import { StoreProvider } from "./Store";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { usePartnerStack } from "~/hooks/usePartnerStack";
import { useUTM } from "~/hooks/useUTM";
import { StatsigProvider } from "@statsig/react-bindings";
import { useAccount } from "~/hooks/useAccount";
import { statsigClientKey } from "~/helpers/environment";

type Props = {
  children: ReactNode | ReactPortal;
};

const StatsigAppProvider = ({ children }: Props) => {
  const { account } = useAccount();

  const statsigUser = useMemo(
    () => ({
      userID: account ? String(account.id) : "anonymous",
      email: account?.email,
      custom: {
        signup_origin: account?.signup_origin,
        payment_provider: account?.payment_provider,
      },
    }),
    [account]
  );

  if (!statsigClientKey) {
    return <>{children}</>;
  }

  return (
    <StatsigProvider sdkKey={statsigClientKey} user={statsigUser}>
      {children}
    </StatsigProvider>
  );
};

export const ContextWrapper = ({ children }: Props) => {
  const { init } = usePartnerStack();
  useUTM();

  useEffect(() => {
    const script = init();
    if (!script) return;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  return (
    <GoogleOAuthProvider clientId={import.meta.env.VITE_GOOGLE_OAUTH_ID}>
      <AccountProvider>
        <ReactQueryProvider>
          <StoreProvider>
            <StatsigAppProvider>{children}</StatsigAppProvider>
          </StoreProvider>
        </ReactQueryProvider>
      </AccountProvider>
    </GoogleOAuthProvider>
  );
};
